import { TreeSelect as AntTreeSelect } from 'antd';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/common';
import { Link as RouterLink } from 'react-router-dom';
import './TreeSelect.css';
import { colours } from 'utils/theme';

const { TreeNode } = AntTreeSelect;

const AntTreeSelectContainer = styled(AntTreeSelect)`
  .ant-select-tree-list-holder {
    overscroll-behavior: contain;
  }
`;

// Create a container with fixed positioning for the link icon
const LinkIconContainer = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
`;

const TreeSelect = ({
  treeData,
  'data-testid': dataTestId,
  onCreateNewItem,
  buttonText,
  antSelectRef,
  selectedValues,
  maxCount,
  tagRender,
  onDropdownBlur,
  resetScrollPositionOnHide,
  onDropdownVisibleChange,
  virtual = false,
  titleIconStyle,
  titleTextStyle,
  ...rest
}) => {
  const refSelect = useRef();
  const [dropdownWidth, setDropdownWidth] = useState(500);

  const handleDropdownVisibleChange = visible => {
    if (visible && refSelect.current) {
      // Ensure dropdown is rendered and then calculate width
      setTimeout(() => {
        const dropdown = document.querySelector('.ant-select-dropdown');
        if (dropdown) {
          const width = dropdown.offsetWidth;
          setDropdownWidth(width - 100);
        }
      }, 0);

      // Reset scroll position if needed
      if (resetScrollPositionOnHide) {
        requestAnimationFrame(() => refSelect.current.scrollTo({ index: 0 }));
      }
    }

    // Handle dropdown close
    if (!visible && onDropdownBlur) {
      onDropdownBlur();
    }

    // Call the passed onDropdownVisibleChange prop if it exists
    onDropdownVisibleChange?.(visible);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  };

  const getTitleElement = (item, isSelected) => {
    // Add null check to handle undefined title
    const itemTitle = item?.title || '';
    const isParentTitleRegex = /(.*)\((\d+)\)(.*)/;
    const match = itemTitle.match(isParentTitleRegex);
    const title = match ? match[1] : itemTitle;
    const numChildren = match ? match[2] : null;

    const titleStyle = {
      fontSize: '14px',
      flex: 1,
      minWidth: 0, // Allows the span to shrink
      maxWidth: `${dropdownWidth}px`, // Apply the calculated dropdown width
      overflow: 'hidden',
      whiteSpace: 'normal',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
    };

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          paddingRight: item.displayLinks ? '32px' : '0', // Add padding for the link icon
        }}
        title={itemTitle}
      >
        <span style={{ ...titleStyle, ...(titleTextStyle || {}) }}>
          {item.icon && (
            <Icon
              icon={item.icon}
              style={{
                marginRight: '6px',
                ...(titleIconStyle || {}),
                ...(titleIconStyle
                  ? {}
                  : {
                      fontSize: 14,
                      width: 16,
                      height: 16,
                      verticalAlign: 'middle',
                      display: 'inline-block',
                      color: colours.grey,
                      position: 'relative',
                    }),
              }}
            />
          )}
          {item.displayTitle || title}
          {numChildren && <span style={{ opacity: 0.5 }}>({numChildren})</span>}
        </span>
        {!item.children && item.displayLinks && (
          <LinkIconContainer>
            <RouterLink
              to={item.link}
              target={'_blank'}
              rel='noopener noreferrer'
              onClick={e => e.stopPropagation()}
            >
              <Icon
                icon='link' // Outgoing link icon
                size='sm'
                style={{
                  cursor: 'pointer',
                  color: isSelected ? ' #1890FF' : colours.grey,
                }}
              />
            </RouterLink>
          </LinkIconContainer>
        )}
        {isSelected && !item.displayLinks && (
          <Icon
            icon='check'
            color='#1890ff'
            size='sm'
            style={{
              flexShrink: 0,
              marginLeft: '8px',
            }}
          />
        )}
      </div>
    );
  };

  const defaultTagRender = ({ label, value, closable, onClose }) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          backgroundColor: colours.teal,
          textTransform: 'uppercase',
          color: 'white',
          cursor: 'default',
          margin: '5px 5px 5px 0px',
          padding: '0px 5px',
          fontSize: '10px',
          borderRadius: '3px',
          fontWeight: '500',
        }}
        title={label.title ?? value}
      >
        <span
          style={{
            flex: 1,
            maxWidth: `${dropdownWidth}px`,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {label.label ?? label}
        </span>
        {closable && (
          <Icon
            icon='delete'
            size='lg'
            style={{
              marginLeft: '8px',
              cursor: 'pointer',
            }}
            onClick={onClose}
          />
        )}
      </div>
    );
  };

  const renderTreeNodes = data =>
    data.map(item => {
      if (!item) return null;

      const isSelected = selectedValues
        ? selectedValues.includes(item.key) ||
          selectedValues.some(v => v?.value && v.value.includes(item.key))
        : false;
      const titleElement = getTitleElement(item, isSelected);

      return item?.children && Array.isArray(item.children) ? (
        <TreeNode
          title={titleElement}
          key={item.key}
          value={item.value}
          selectable={item.selectable !== false}
          checkable={item.checkable !== false}
        >
          {renderTreeNodes(item.children)}
        </TreeNode>
      ) : (
        <TreeNode
          title={titleElement}
          key={item.key}
          value={item.value}
          label={item ?? titleElement}
          disabled={
            item.disabled ||
            (maxCount && selectedValues?.length >= maxCount && !isSelected)
          }
        />
      );
    });

  return (
    <AntTreeSelectContainer
      {...rest}
      value={selectedValues}
      ref={refSelect}
      showCheckedStrategy={AntTreeSelect.SHOW_PARENT}
      data-testid={dataTestId}
      tagRender={tagRender ?? defaultTagRender}
      virtual={virtual && !isFirefox()}
      treeNodeLabelProp={'label'}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      getPopupContainer={triggerNode => triggerNode.parentElement}
    >
      {renderTreeNodes(treeData)}
    </AntTreeSelectContainer>
  );
};

export default TreeSelect;
